import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceMascot, MascotExpression } from '../invoice-tour/invoice-mascot.component';
import './signup-guide.style.scss';

export interface SignupGuideContext {
    /** Which screen the character is on — changes the idle line. */
    variant?: 'signup' | 'signin';
    /** 1-based wizard step, used for the message shown when nothing is focused. */
    step?: number;
    /** Name of the field the user is currently in, or null. */
    activeField: string | null;
    /** Current values — read only, the guide never writes back. */
    values?: {
        email?: string;
        password?: string;
        socialSecurityNumber?: string;
        dateOfBirth?: string;
        phoneNo?: string;
        zipCode?: string;
    };
    /** Per-field "looks good" flags already computed by the page. */
    valid?: Record<string, boolean>;
    /**
     * Something went wrong and the character should say so. `blocking` is for
     * "you can't continue yet" (a rejected Next / submit); without it the
     * message is a field the user is currently getting wrong.
     */
    alert?: {
        /** Translation key, or an already-translated sentence. */
        message: string;
        blocking?: boolean;
        /** 'success' paints the cloud green; anything else reads as an error. */
        kind?: 'error' | 'success';
        /** When it was raised — keys the cloud so an identical message repeats
         *  its attention animation instead of sitting there silently. */
        at?: number;
    } | null;
    /** Live rule results for the fields that have them. */
    flags?: {
        ssnZeros?: boolean;
        dobBadDate?: boolean;
        phoneBad?: boolean;
        postalBad?: boolean;
        emailBad?: boolean;
        passwordBad?: boolean;
    };
}

interface Message {
    key: string;
    params?: Record<string, unknown>;
    mood: MascotExpression;
}

/** Which line the character says, given what the user is doing right now. */
const resolveMessage = (ctx: SignupGuideContext): Message => {
    const { activeField } = ctx;

    /* an error always wins — the character stops explaining the field and
       tells the user what is actually wrong */
    if (ctx.alert) {
        if (ctx.alert.kind === 'success') {
            return { key: ctx.alert.message, mood: 'happy' };
        }
        return ctx.alert.blocking
            ? { key: 'GUIDE_BLOCKED', params: { reason: ctx.alert.message }, mood: 'sad' }
            : { key: ctx.alert.message, mood: 'sad' };
    }

    const values = { email: '', password: '', socialSecurityNumber: '', dateOfBirth: '', phoneNo: '', zipCode: '', ...(ctx.values || {}) };
    const valid = ctx.valid || {};
    const flags = ctx.flags || {};
    const isSignin = ctx.variant === 'signin';

    switch (activeField) {
        case 'accountType':
            return { key: 'GUIDE_ACCOUNT_TYPE', mood: 'idle' };

        case 'firstName':
        case 'lastName':
            return { key: 'GUIDE_NAME', mood: 'idle' };

        case 'email':
            if (isSignin) return { key: 'GUIDE_SIGNIN_EMAIL', mood: 'idle' };
            if (!values.email) return { key: 'GUIDE_EMAIL', mood: 'idle' };
            if (valid.email) return { key: 'GUIDE_EMAIL_OK', mood: 'happy' };
            if (flags.emailBad) return { key: 'GUIDE_EMAIL_BAD', mood: 'confused' };
            return { key: 'GUIDE_EMAIL', mood: 'idle' };

        case 'password':
            if (isSignin) return { key: 'GUIDE_SIGNIN_PASSWORD', mood: 'idle' };
            if (!values.password) return { key: 'GUIDE_PASSWORD', mood: 'idle' };
            if (valid.password) return { key: 'GUIDE_PASSWORD_OK', mood: 'happy' };
            if (flags.passwordBad) return { key: 'GUIDE_PASSWORD_BAD', mood: 'confused' };
            return { key: 'GUIDE_PASSWORD', mood: 'idle' };

        case 'socialSecurityNumber': {
            const digits = values.socialSecurityNumber.length;
            if (!digits) return { key: 'GUIDE_SSN', mood: 'idle' };
            if (flags.ssnZeros) return { key: 'GUIDE_SSN_ZEROS', mood: 'sad' };
            if (digits === 12) return { key: 'GUIDE_SSN_OK', mood: 'happy' };
            return { key: 'GUIDE_SSN_PROGRESS', params: { count: digits }, mood: 'idle' };
        }

        case 'dateOfBirth': {
            const digits = values.dateOfBirth.replace(/\D/g, '').slice(0, 8).length;
            if (!digits) return { key: 'GUIDE_DOB', mood: 'idle' };
            if (flags.dobBadDate) return { key: 'GUIDE_DOB_BAD', mood: 'confused' };
            if (valid.dateOfBirth) return { key: 'GUIDE_DOB_OK', mood: 'happy' };
            return { key: 'GUIDE_DOB', mood: 'idle' };
        }

        case 'profession':
            return { key: 'GUIDE_PROFESSION', mood: 'idle' };

        case 'phoneNo':
            if (flags.phoneBad) return { key: 'GUIDE_PHONE_BAD', mood: 'confused' };
            if (valid.phoneNo) return { key: 'GUIDE_PHONE_OK', mood: 'happy' };
            return { key: 'GUIDE_PHONE', mood: 'idle' };

        case 'address':
            return { key: 'GUIDE_ADDRESS', mood: 'idle' };

        case 'zipCode':
            if (flags.postalBad) return { key: 'GUIDE_POSTAL_BAD', mood: 'confused' };
            return { key: 'GUIDE_POSTAL', mood: 'idle' };

        case 'city':
            return { key: 'GUIDE_CITY', mood: 'idle' };

        case 'country':
            return { key: 'GUIDE_COUNTRY', mood: 'idle' };

        case 'agreeToTerms':
            return { key: 'GUIDE_TERMS', mood: 'idle' };

        /* ── BankID choices, links and mode switches ─────────────────── */
        case 'bankidDevice':
            return { key: 'GUIDE_BANKID_DEVICE', mood: 'idle' };

        case 'bankidQr':
            return { key: 'GUIDE_BANKID_QR', mood: 'idle' };

        case 'bankidWaiting':
            return { key: 'GUIDE_BANKID_WAITING', mood: 'happy' };

        /* the QR is on screen — walk them through scanning it */
        case 'bankidQrOpen':
            return {
                key: isSignin ? 'GUIDE_BANKID_QR_OPEN' : 'GUIDE_BANKID_QR_OPEN_SIGNUP',
                mood: 'happy',
            };

        case 'switchEmail':
            return {
                key: isSignin ? 'GUIDE_SWITCH_EMAIL' : 'GUIDE_SWITCH_EMAIL_SIGNUP',
                mood: 'idle',
            };

        case 'switchBankid':
            return { key: 'GUIDE_SWITCH_BANKID', mood: 'idle' };

        case 'signupLink':
            return { key: 'GUIDE_SIGNUP_LINK', mood: 'idle' };

        case 'signinLink':
            return { key: 'GUIDE_SIGNIN_LINK', mood: 'idle' };

        case 'rememberMe':
            return { key: 'GUIDE_REMEMBER_ME', mood: 'idle' };

        case 'forgotPassword':
            return { key: 'GUIDE_FORGOT_PASSWORD', mood: 'idle' };

        /* hovering the final button: cheer them on if everything is in place,
           otherwise say plainly that something is still missing */
        case 'submit':
            if (isSignin) return { key: 'GUIDE_SIGNIN_SUBMIT', mood: 'happy' };
            return valid.readyToSubmit
                ? { key: 'GUIDE_SUBMIT_READY', mood: 'happy' }
                : { key: 'GUIDE_SUBMIT_INCOMPLETE', mood: 'confused' };

        default:
            if (isSignin) return { key: 'GUIDE_SIGNIN_DEFAULT', mood: 'idle' };
            return {
                key: `GUIDE_STEP_${ctx.step || 1}`,
                /* the last step is the finish line — cheer there, not before */
                mood: ctx.step === 4 ? 'happy' : 'idle',
            };
    }
};

/**
 * The Invozio character that walks the user through registration: it explains
 * the field currently in focus and reacts to what has been typed.
 * Presentational only — it reads state, never changes it.
 */
export const SignupGuide: React.FC<{
    context: SignupGuideContext;
    /** 'panel' = large character beside the form, 'inline' = compact strip. */
    layout?: 'panel' | 'inline';
    className?: string;
}> = ({ context, layout = 'inline', className }) => {
    const { t } = useTranslation();
    const message = resolveMessage(context);
    const text = t(message.key, message.params) as string;

    const eyesRef = useRef<SVGGElement | null>(null);
    const charRef = useRef<HTMLSpanElement | null>(null);

    /* Easter egg: poke the character. Tapping its face makes it furious,
       tapping below sets it off crying — both wear off on their own. */
    const [poke, setPoke] = useState<'angry' | 'cry' | null>(null);
    const pokeTimer = useRef<number | undefined>(undefined);

    const pokeCharacter = (kind: 'angry' | 'cry') => {
        window.clearTimeout(pokeTimer.current);
        setPoke(kind);
        pokeTimer.current = window.setTimeout(() => setPoke(null), kind === 'cry' ? 3400 : 2600);
    };

    useEffect(() => () => window.clearTimeout(pokeTimer.current), []);

    /* Type the line out character by character, exactly like the dashboard
       narrator — the mouth animates while there is still text to come. */
    const [typed, setTyped] = useState('');

    useEffect(() => {
        const chars = Array.from(text); // Array.from → don't split emoji
        setTyped('');
        let i = 0;
        const typeId = window.setInterval(() => {
            i += 1;
            setTyped(chars.slice(0, i).join(''));
            if (i >= chars.length) window.clearInterval(typeId);
        }, 38);
        return () => window.clearInterval(typeId);
    }, [text]);

    const talking = typed.length < Array.from(text).length;
    const expression = poke ? 'sad' : message.mood;

    /* the character's eyes follow the cursor, as on the dashboard */
    useEffect(() => {
        const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

        const onMove = (e: MouseEvent) => {
            const box = charRef.current?.getBoundingClientRect();
            if (!box || !eyesRef.current) return;
            const originX = box.left + box.width / 2;
            const originY = box.top + box.height * 0.42;
            const dx = clamp((e.clientX - originX) / 260, -1, 1);
            const dy = clamp((e.clientY - originY) / 260, -1, 1);
            eyesRef.current.style.transform = `translate(${dx * 2.8}px, ${dy * 2.4}px)`;
        };

        window.addEventListener('mousemove', onMove);
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    const isAlert = !!context.alert && context.alert.kind !== 'success';
    const isCheer = context.alert?.kind === 'success';

    return (
        <div
            className={[
                'inv-signup-guide',
                `inv-signup-guide--${layout}`,
                isAlert ? 'is-alert' : '',
                poke === 'cry' ? 'is-crying' : poke === 'angry' ? 'is-angry' : '',
                isCheer ? 'is-cheer' : '',
                context.alert?.blocking ? 'is-blocking' : '',
                className || '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <span className="inv-signup-guide__bot" ref={charRef}>
                <InvoiceMascot
                    expression={expression}
                    talking={talking && !poke}
                    eyesRef={eyesRef}
                />

                {poke === 'angry' && (
                    <>
                        <span className="inv-signup-guide__brow inv-signup-guide__brow--left" aria-hidden="true" />
                        <span className="inv-signup-guide__brow inv-signup-guide__brow--right" aria-hidden="true" />
                        <span className="inv-signup-guide__fume" aria-hidden="true" />
                    </>
                )}

                {layout === 'panel' && (
                    <>
                        <span className="inv-signup-guide__trail inv-signup-guide__trail--1" aria-hidden="true" />
                        <span className="inv-signup-guide__trail inv-signup-guide__trail--2" aria-hidden="true" />
                    </>
                )}

                {poke === 'cry' && (
                    <>
                        <span className="inv-signup-guide__tear inv-signup-guide__tear--left" aria-hidden="true" />
                        <span className="inv-signup-guide__tear inv-signup-guide__tear--right" aria-hidden="true" />
                    </>
                )}

                {/* decorative hit areas — face on top, body below */}
                <span
                    className="inv-signup-guide__poke inv-signup-guide__poke--face"
                    aria-hidden="true"
                    onClick={() => pokeCharacter('angry')}
                />
                <span
                    className="inv-signup-guide__poke inv-signup-guide__poke--body"
                    aria-hidden="true"
                    onClick={() => pokeCharacter('cry')}
                />
            </span>

            {/* the full line for screen readers — the typed one is decorative */}
            <span className="inv-signup-guide__sr" role="status" aria-live="polite">
                {text}
            </span>

            {layout === 'panel' ? (
                /* speech cloud — overlapping puffs build the fluffy silhouette,
                   the trailing dots point back at the character */
                <div className="inv-signup-guide__cloud" key={context.alert?.at ?? message.key}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                        <span key={n} className={`inv-signup-guide__puff inv-signup-guide__puff--${n}`} aria-hidden="true" />
                    ))}

                    <p className="inv-signup-guide__text" aria-hidden="true">
                        {typed}
                    </p>
                </div>
            ) : (
                <p className="inv-signup-guide__bubble" aria-hidden="true" key={context.alert?.at ?? message.key}>
                    {typed}
                </p>
            )}
        </div>
    );
};

export default SignupGuide;
